/**
 * Route transition capture.
 * Listens to popstate and intercepts pushState/replaceState
 * to maintain a history of the last N route transitions.
 */

import type { RouteEntry } from '../types';
import { scrubPII } from '../pii/index';

const DEFAULT_MAX_ENTRIES = 20;

export class RouteCapture {
  private static readonly SENSITIVE_PARAMS = ['code', 'token', 'reset_token', 'state', 'access_token',
    'id_token', 'session', 'key', 'secret', 'password', 'email'];

  private entries: RouteEntry[] = [];
  private maxEntries: number;
  private installed = false;
  private originalPushState: typeof history.pushState | null = null;
  private originalReplaceState: typeof history.replaceState | null = null;
  private popstateHandler: ((event: PopStateEvent) => void) | null = null;

  constructor(maxEntries: number = DEFAULT_MAX_ENTRIES) {
    this.maxEntries = maxEntries;
  }

  /** Install route capture listeners */
  install(): void {
    if (this.installed) return;
    if (typeof window === 'undefined' || typeof history === 'undefined') return;

    // Record initial route
    this.recordRoute(window.location.pathname + window.location.search);

    // Intercept pushState
    this.originalPushState = history.pushState.bind(history);
    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      this.originalPushState!(...args);
      this.recordRoute(window.location.pathname + window.location.search);
    };

    // Intercept replaceState
    this.originalReplaceState = history.replaceState.bind(history);
    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      this.originalReplaceState!(...args);
      this.recordRoute(window.location.pathname + window.location.search);
    };

    // Listen to popstate (back/forward navigation)
    this.popstateHandler = () => {
      this.recordRoute(window.location.pathname + window.location.search);
    };
    window.addEventListener('popstate', this.popstateHandler);

    this.installed = true;
  }

  /** Uninstall route capture listeners */
  uninstall(): void {
    if (!this.installed) return;

    if (this.originalPushState) {
      history.pushState = this.originalPushState;
      this.originalPushState = null;
    }

    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }

    if (this.popstateHandler) {
      window.removeEventListener('popstate', this.popstateHandler);
      this.popstateHandler = null;
    }

    this.installed = false;
  }

  /** Sanitize a path by redacting sensitive query parameters and scrubbing PII */
  private sanitizePath(raw: string): string {
    try {
      const url = new URL(raw, 'https://placeholder');
      for (const param of RouteCapture.SENSITIVE_PARAMS) {
        if (url.searchParams.has(param)) url.searchParams.set(param, '[REDACTED]');
      }
      return scrubPII(url.pathname + url.search);
    } catch {
      return scrubPII(raw);
    }
  }

  /** Record a route transition */
  private recordRoute(path: string): void {
    const sanitized = this.sanitizePath(path);
    if (this.entries.length >= this.maxEntries) {
      this.entries.shift();
    }
    this.entries.push({
      path: sanitized,
      timestamp: new Date().toISOString(),
    });
  }

  /** Get a snapshot of route history */
  getEntries(): RouteEntry[] {
    return [...this.entries];
  }

  /** Clear route history */
  clear(): void {
    this.entries = [];
  }
}
